<template>
  <div class="st-suggest">
    <StTextInput
      ref="inputEl"
      v-model="model"
      v-bind="$attrs"
      :placeholder="placeholder"
      @focus="open"
      @input="open"
      @keydown="onKeydown"
      @blur="onBlur"
    />
    <!-- Teleported to <body> with fixed positioning so the menu is never clipped by
         an ancestor's overflow (e.g. a scrolling editor surface). -->
    <Teleport to="body">
      <transition name="st-dropdown">
        <ul
          v-if="isOpen && filtered.length"
          class="st-suggest__menu"
          role="listbox"
          :style="menuStyle"
        >
          <li
            v-for="(s, i) in filtered"
            :key="s"
            class="st-suggest__option"
            :class="{ 'is-active': i === activeIndex }"
            role="option"
            :aria-selected="i === activeIndex"
            @mousedown.prevent="choose(s)"
            @mouseenter="activeIndex = i"
          >
            <span class="st-suggest__label">{{ s }}</span>
            <!-- Chrome-style remove: forget this suggestion so it stops being offered.
                 mousedown is stopped so it doesn't trigger the row's choose handler. -->
            <StIconButton
              class="st-suggest__remove"
              icon="xmark"
              :label="`Remove suggestion ${s}`"
              size="sm"
              variant="danger"
              @mousedown.stop.prevent
              @click="forget(s)"
            />
          </li>
        </ul>
      </transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
// A text input with a styled suggestion dropdown (replaces the browser's native
// <datalist> popover). Suggestions are filtered by what's typed, navigable with the
// arrow keys, and each can be removed from the remembered set via its X. The parent
// owns what "choosing" a suggestion means (set a field vs. add a chip) via @select.
import { computed, nextTick, onBeforeUnmount, ref, watch, type CSSProperties } from 'vue';
import StTextInput from './StTextInput.vue';
import StIconButton from './StIconButton.vue';

const props = withDefaults(
  defineProps<{
    modelValue: string;
    /** Full suggestion set; the component filters it by the typed text. */
    suggestions: string[];
    placeholder?: string;
    /** Values to hide from the dropdown (e.g. keywords already added). */
    exclude?: string[];
  }>(),
  { placeholder: '', exclude: () => [] },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
  /** A suggestion was picked (click or Enter on the highlighted row). */
  (e: 'select', value: string): void;
  /** The X on a suggestion was clicked — forget it from the remembered set. */
  (e: 'forget', value: string): void;
  /** Enter pressed with no suggestion highlighted (parent decides what to commit). */
  (e: 'enter'): void;
  /** Raw keydown pass-through for parent-specific keys (comma, Backspace, …). */
  (e: 'keydown', event: KeyboardEvent): void;
  (e: 'blur'): void;
}>();

// Attribute fall-through (disabled, etc.) is forwarded onto the inner input, not the
// wrapper div.
defineOptions({ inheritAttrs: false });

const model = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
});

const isOpen = ref(false);
const activeIndex = ref(-1);
const inputEl = ref<InstanceType<typeof StTextInput> | null>(null);
const menuStyle = ref<CSSProperties>({});

// Position the teleported menu under (or above) the input, in viewport coordinates.
// Flips above when there's more room there, so it never spills off-screen.
function updatePosition() {
  const el = inputEl.value?.$el as HTMLElement | undefined;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  const gap = 4;
  const below = window.innerHeight - rect.bottom - gap;
  const above = rect.top - gap;
  const placeAbove = below < 200 && above > below;
  const maxHeight = Math.min(256, Math.max(120, placeAbove ? above : below));
  menuStyle.value = placeAbove
    ? {
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        bottom: `${window.innerHeight - rect.top + gap}px`,
        maxHeight: `${maxHeight}px`,
      }
    : {
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        top: `${rect.bottom + gap}px`,
        maxHeight: `${maxHeight}px`,
      };
}

const filtered = computed(() => {
  const q = props.modelValue.trim().toLowerCase();
  const excluded = new Set(props.exclude.map((e) => e.toLowerCase()));
  return props.suggestions.filter((s) => {
    const low = s.toLowerCase();
    if (excluded.has(low)) return false;
    if (!q) return true;
    // Don't offer the exact value already typed.
    if (low === q) return false;
    return low.includes(q);
  });
});

function focusInput() {
  inputEl.value?.$el?.focus();
}

function open() {
  isOpen.value = true;
}
function close() {
  isOpen.value = false;
  activeIndex.value = -1;
}
function choose(value: string) {
  emit('select', value);
  close();
}
function forget(value: string) {
  emit('forget', value);
  // Keep the box focused/open so the user can keep typing or remove more.
  focusInput();
}
function onBlur() {
  close();
  emit('blur');
}
function onKeydown(e: KeyboardEvent) {
  const count = filtered.value.length;
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    open();
    if (count) activeIndex.value = (activeIndex.value + 1) % count;
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    if (count) activeIndex.value = activeIndex.value <= 0 ? count - 1 : activeIndex.value - 1;
  } else if (e.key === 'Enter') {
    if (isOpen.value && activeIndex.value >= 0 && activeIndex.value < count) {
      e.preventDefault();
      choose(filtered.value[activeIndex.value]);
      return;
    }
    emit('enter');
  } else if (e.key === 'Escape' && isOpen.value) {
    // First Escape just closes the dropdown; don't forward it so the parent (e.g.
    // an add-keyword box) stays open until a second Escape.
    e.preventDefault();
    close();
    return;
  }
  // Always forward so parents can handle their own keys (comma, Backspace, …).
  emit('keydown', e);
}

// Keep the teleported menu pinned to the input while it's open: reposition on any
// ancestor scroll (capture) or window resize, and when the result list changes size.
function onReposition() {
  if (isOpen.value) updatePosition();
}
watch(isOpen, (open) => {
  if (open) {
    nextTick(updatePosition);
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
  } else {
    window.removeEventListener('scroll', onReposition, true);
    window.removeEventListener('resize', onReposition);
  }
});
watch(filtered, () => {
  if (isOpen.value) nextTick(updatePosition);
});
onBeforeUnmount(() => {
  window.removeEventListener('scroll', onReposition, true);
  window.removeEventListener('resize', onReposition);
});

defineExpose({ focus: focusInput, close });
</script>

<style scoped>
.st-suggest {
  position: relative;
  display: flex;
  flex: 1;
  min-width: 0;
}
.st-suggest :deep(.st-control) {
  flex: 1;
  min-width: 0;
}

/* Fixed + teleported to <body>; left/width/top|bottom/max-height come from inline
   style (updatePosition) so no ancestor overflow can clip it. */
.st-suggest__menu {
  position: fixed;
  z-index: var(--st-z-popover, 1000);
  overflow-y: auto;
  padding: var(--st-space-1);
  list-style: none;
  background: var(--st-surface);
  border: 1px solid var(--st-border);
  border-radius: var(--st-radius-md);
  box-shadow: var(--st-shadow-popover);
}

.st-suggest__option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--st-space-1);
  padding: var(--st-space-1) var(--st-space-1) var(--st-space-1) var(--st-space-2);
  border-radius: var(--st-radius-sm);
  font-size: var(--st-text-sm);
  color: var(--st-text);
  cursor: pointer;
}
.st-suggest__option.is-active {
  background: var(--st-surface-hover);
}
.st-suggest__label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* X stays subtle until the row is hovered/active, like a browser's history menu. */
.st-suggest__remove {
  opacity: 0;
  flex: none;
}
.st-suggest__option:hover .st-suggest__remove,
.st-suggest__option.is-active .st-suggest__remove,
.st-suggest__remove:focus-visible {
  opacity: 1;
}

.st-dropdown-enter-active,
.st-dropdown-leave-active {
  transition:
    opacity var(--st-transition-fast) var(--st-ease),
    transform var(--st-transition-fast) var(--st-ease);
}
.st-dropdown-enter-from,
.st-dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
